import { Icon, Tooltip } from '@mui/material';
import { useMemo, useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { Text } from './typography';
import styles from './UserMultiSelector.module.scss';
import SimpleUserCard from './SimpleUserCard';
import { closeModalComponents } from '../utilities/modal';
import { useTranslation } from 'react-i18next';
import { keys } from '../utilities/translator/translation_keys';
import { useWorkspacePermissions } from '../providers/WorkspacePermissionsProvider';
import { useWorkspace } from '../providers/WorkspaceProvider';
import { useCurrentUser } from '../providers/UserProvider';
import { useWorkspaceMutations } from '../hooks/addUserToWorkspace';
import { useModal } from '../providers/ModalProvider';
import { customColors, sortAlphabeticalOrder } from '../utilities';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { useQueryParams } from '../hooks/misc';
import PortalMenu from '../utilities/Portal/PortalMenu';
import MultiAvatar from './MultiAvatar';
import Spacer from './typography/Spacer';

const bulkAddUsersMutation = loader('./UserMultiSelector.add.graphql');
const bulkRemoveUsersMutation = loader('./UserMultiSelector.remove.graphql');

export default function UserMultiSelector({
  selected = [],
  onUserAdded,
  type,
  onUserRemoved,
  readOnly = false,
  placeholder,
  usersToLeaveOut = [],
  otherWorkspace = null,
  defaultAllSelected = false,
  parentId = null,
  parentType = '',
  setUsers = () => {},
  stateUpdateOnly = false,
  disableSelectAll = false,
  weight = 'regular',
  iconDisplay = false,
  className,
  menuStyle = {},
  maxMenuHeight = null,
  otherWorkspaces = [],
  refetch = () => {},
  // Optional overrides for select-all, for parents that aren't on the
  // polymorphic participant table (e.g. roles) — when provided, these run
  // instead of the bulk participant mutations
  onBulkAdd,
  onBulkRemove,
}) {
  const online = useOnlineStatus();
  const [bulkAddUsers] = useMutation(bulkAddUsersMutation, {
    onCompleted: refetch,
  });
  const [bulkRemoveUsers] = useMutation(bulkRemoveUsersMutation, {
    onCompleted: refetch,
  });
  const { t } = useTranslation();
  const {
    allWorkspaceUsers,
    allUsers,
    companyAdmins,
    isWorkspaceAdmin,
    refetchAllUsers,
  } = useWorkspacePermissions();
  const [search, setSearch] = useState('');
  const { modalIsOpen } = useModal();
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const { isAdmin, user: currentUser } = useCurrentUser();
  const { workspace, companyPermissions, refetchWorkspacePermission } =
    useWorkspace();
  const { addUser } = useWorkspaceMutations();
  const { openConfirmationModal } = useModal();
  const { getParam } = useQueryParams();
  const allWorkspacesParam = getParam('allWorkspaces') === 'TRUE';
  const showAllUsers = useMemo(
    () =>
      otherWorkspace?.id === 'company_shared' ||
      type === 'all' ||
      (allWorkspacesParam && !modalIsOpen && type !== 'workspace'),
    [allWorkspacesParam, modalIsOpen, otherWorkspace?.id, type],
  );
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    closeModalComponents(ref, setModalOpen);
  }, [ref]);

  useEffect(() => {
    if (!modalOpen) {
      setSearch('');
    } else {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [modalOpen]);

  const workspaceFilteredUsers = useMemo(() => {
    let workspaceUsers = [...companyAdmins];
    if (!!otherWorkspace?.id && !!otherWorkspace?.id !== 'company_shared') {
      const o = companyPermissions
        ?.filter((p) => p.workspaceId === otherWorkspace?.id)
        ?.map(({ user }) => allUsers?.find((u) => u.id === user.id) || user);
      if (o && o.length) workspaceUsers = workspaceUsers.concat(o);
    } else if (allWorkspaceUsers && allWorkspaceUsers.length) {
      workspaceUsers = workspaceUsers.concat(
        allWorkspaceUsers.map((u) => allUsers?.find((a) => a.id === u.id) || u),
      );
    }

    const userMap = new Map();
    workspaceUsers.forEach((user) => {
      userMap.set(user.id, user);
    });

    const uniqueWorkspaceUsers = Array.from(userMap.values());

    const filteredUsers = search?.length
      ? uniqueWorkspaceUsers.filter((user) => {
          const userString = `${user.firstName} ${user.lastName}`;
          const roleString =
            user?.workRoles?.map((r) => r.title)?.join(',') ?? '';
          return (
            userString.toLowerCase().includes(search.toLowerCase()) ||
            roleString.toLowerCase().includes(search.toLowerCase())
          );
        })
      : uniqueWorkspaceUsers;

    const f = filteredUsers
      ?.filter((l) => !selected?.some((m) => m?.id === l?.id))
      ?.filter((u) => !usersToLeaveOut?.some((s) => s?.id === u?.id));

    const sorted = sortAlphabeticalOrder(f, 'firstName');
    return sorted || f;
  }, [
    allWorkspaceUsers,
    companyAdmins,
    companyPermissions,
    otherWorkspace?.id,
    search,
    selected,
    usersToLeaveOut,
    allUsers,
  ]);

  const multiWorkspaceUsers = useMemo(() => {
    if (!otherWorkspaces?.length) return null;
    const filterUser = (u) =>
      !selected?.some((s) => s?.id === u.id) &&
      !usersToLeaveOut?.some((s) => s?.id === u.id) &&
      (!search?.length ||
        `${u.firstName} ${u.lastName}`
          .toLowerCase()
          .includes(search.toLowerCase()));
    return otherWorkspaces.map((ws) => {
      const users =
        companyPermissions
          ?.filter((p) => p.workspaceId === ws.id)
          ?.map(({ user }) => user)
          ?.filter(filterUser) ?? [];
      return {
        workspace: ws,
        users: sortAlphabeticalOrder(users, 'firstName'),
      };
    });
  }, [otherWorkspaces, companyPermissions, search, selected, usersToLeaveOut]);

  const allFilteredUsers = useMemo(() => {
    const u = (
      search?.length
        ? allUsers?.filter((user) => {
            const userString = `${user.firstName} ${user.lastName}`;
            const roleString = user?.workRoles?.map((r) => r.title)?.join(',');
            return (
              userString.toLowerCase().includes(search.toLowerCase()) ||
              roleString.toLowerCase().includes(search.toLowerCase())
            );
          })
        : allUsers || []
    )
      .filter((l) => !selected?.some((m) => m?.id === l.id))
      .filter((u) => !usersToLeaveOut?.some((s) => s?.id === u.id));
    const sorted = sortAlphabeticalOrder(u) || u;
    return sorted;
  }, [search, allUsers, selected, usersToLeaveOut]);

  const allUsersSelected = useMemo(() => {
    if (selected?.length > 0) {
      return !showAllUsers
        ? workspaceFilteredUsers?.length < 1
        : allFilteredUsers?.length < 1;
    }
    return false;
  }, [
    selected?.length,
    showAllUsers,
    workspaceFilteredUsers?.length,
    allFilteredUsers?.length,
  ]);

  const usersWithAddOption = useMemo(() => {
    const u = allFilteredUsers?.filter(
      (user) =>
        !workspaceFilteredUsers?.some((wsUser) => wsUser.id === user.id),
    );
    const sorted = sortAlphabeticalOrder(u);
    return sorted || u;
  }, [allFilteredUsers, workspaceFilteredUsers]);

  const userCard = (user, onUserAdded, notInWorkspace = false) => {
    const hasRoles = user?.workRoles?.length > 0;

    const roleString = hasRoles
      ? user?.workRoles?.map(({ title }) => title)?.join(', ')
      : '';
    return (
      <div
        key={user.id}
        className={styles.userContainer}
        onClick={() => {
          onUserAdded(user);
        }}
      >
        <SimpleUserCard
          user={user}
          size="sm"
          smallText={roleString}
          smallTextSize="xs"
          disableClick
          wordsToHighlight={search ? [search.toLowerCase()] : []}
        />
        {notInWorkspace ? (
          <Icon style={{ fontSize: '1rem' }} className={styles.addIcon}>
            add
          </Icon>
        ) : null}
      </div>
    );
  };

  const [defaultUsersSelected, setDefaultUsersSelected] = useState(false);

  useEffect(() => {
    if (!online || disableSelectAll) {
      return;
    }
    const targetUsers = !showAllUsers
      ? workspaceFilteredUsers
      : allFilteredUsers;

    if (!showAllUsers) {
      const uniqueWorkspaceUsersMap = new Map();
      for (const user of [...allWorkspaceUsers, ...companyAdmins]) {
        uniqueWorkspaceUsersMap.set(user.id, user);
      }
    }

    if (defaultAllSelected && !defaultUsersSelected) {
      if (!stateUpdateOnly) {
        bulkAddUsers({
          variables: {
            userIds: targetUsers?.map(({ id: userId }) => userId),
            parentId,
            parentType,
          },
        });
      }
      setUsers((prev) => [...prev, ...targetUsers]);
      setDefaultUsersSelected(true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultAllSelected,
    type,
    workspaceFilteredUsers,
    allFilteredUsers,
    allUsersSelected,
    selected,
  ]);

  const selectedUsers = useMemo(() => {
    return (selected ?? [])
      .filter(Boolean)
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
  }, [selected]);

  const userWorkspaceStatus = (userId) => {
    const userInfo = allUsers?.find((a) => a.id === userId) || [];
    const wsId = otherWorkspace?.id || workspace?.id;
    const userWorkspaces = userInfo.workspaces?.map((w) => w.id);
    return showAllUsers ||
      userInfo.role === 'ADMIN' ||
      !online ||
      userId === currentUser
      ? true
      : userWorkspaces?.includes(wsId);
  };
  const containerRef = useRef(null);

  const handleModalOpen = (e) => {
    if (!readOnly) setModalOpen(true);
    const rect = containerRef.current.getBoundingClientRect();
    const openUpward = window.innerHeight - rect.bottom < 300;
    setMenuPosition(
      !modalOpen
        ? {
            top: (openUpward ? rect.top : rect.bottom) + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            transform: openUpward ? 'translateY(-100%)' : undefined,
            ...(iconDisplay ? menuStyle : {}),
          }
        : {},
    );
  };

  return (
    <div ref={containerRef}>
      {iconDisplay ? (
        <div
          className={className || styles.iconContainer}
          style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
        >
          {selected?.length > 0 && (
            <div onClick={handleModalOpen} style={{ cursor: 'pointer' }}>
              <MultiAvatar
                users={selected}
                size="sm"
                maxLength={3}
                onUserRemoved={!readOnly ? onUserRemoved : undefined}
              />
            </div>
          )}
          <Icon
            className={styles.hover}
            baseClassName="material-icons-outlined"
            sx={{
              fontSize: '1.2rem',
              color: customColors.BLUE,
              cursor: 'pointer',
            }}
            onClick={handleModalOpen}
          >
            {selected.length ? 'add' : 'person_add'}
          </Icon>
        </div>
      ) : selected?.length ? (
        <div className={styles.selectedUserContainer}>
          {selectedUsers.map((user) => {
            const inWorkspace = !!userWorkspaceStatus(user.id);
            return (
              <div className={styles.userCard} key={user.id}>
                <SimpleUserCard
                  user={user}
                  size="sm"
                  weight={weight}
                  color={!inWorkspace ? 'red' : 'primary'}
                  disableClick
                />
                {!inWorkspace ? (
                  <Tooltip
                    title={`No Access to ${
                      otherWorkspace?.title || workspace?.title
                    } `}
                  >
                    <Icon
                      style={{
                        color: customColors.RED_LIGHT,
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                      }}
                      onClick={() => {
                        if (isAdmin || isWorkspaceAdmin) {
                          openConfirmationModal({
                            variant: 'warning',
                            buttonText: 'Add to Workspace',
                            title: `User Workspace Permission`,
                            description: `${user.firstName} ${
                              user.lastName
                            } does not have workspace permissions for ${
                              otherWorkspace?.title || workspace.title
                            } and will not have access. Would you like to add them?`,
                            onSubmit: () => {
                              addUser({
                                userId: user.id,
                                workspaceId:
                                  otherWorkspace?.id || workspace?.id,
                              }).then(async () => {
                                await refetchWorkspacePermission();
                                await refetchAllUsers();
                              });
                            },
                          });
                        }
                      }}
                    >
                      error
                    </Icon>
                  </Tooltip>
                ) : null}
                {!readOnly && !user.readOnly && (
                  <Icon
                    className={styles.closeIcon}
                    fontSize="inherit"
                    baseClassName="material-icons-outlined"
                    onClick={() => {
                      onUserRemoved(user);
                    }}
                  >
                    close
                  </Icon>
                )}
              </div>
            );
          })}
          {!modalOpen && (
            <div className={styles.addButton}>
              {!readOnly && (
                <Icon
                  className={styles.addIcon}
                  baseClassName="material-icons-outlined"
                  onClick={handleModalOpen}
                >
                  add
                </Icon>
              )}
            </div>
          )}
        </div>
      ) : !readOnly ? (
        <div className={styles.buttonContainer} onClick={handleModalOpen}>
          <Icon
            style={{ color: customColors.SECONDARY_LIGHT }}
            baseClassName="material-icons-outlined"
          >
            person
          </Icon>
          <Text size="md" color="secondary" noSelect noMargin>
            {t(keys.action.ADD, {
              variable: placeholder || t(keys.common.USERS),
            })}
          </Text>
        </div>
      ) : (
        <Text noMargin>{t(keys.common.NONE)}</Text>
      )}
      {modalOpen && (
        <PortalMenu open={modalOpen} position={menuPosition}>
          <div
            ref={ref}
            className={iconDisplay ? styles.iconModal : styles.modalContainer}
          >
            <div className={styles.modalHeader}>
              <div className={styles.fullWidth}>
                <Form.Control
                  ref={searchRef}
                  className={styles.input}
                  type="text"
                  placeholder={t(keys.action.SEARCH, {
                    variable: t(keys.common.USERS),
                  })}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Icon
                className={styles.closeIcon}
                fontSize="inherit"
                baseClassName="material-icons-outlined"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                close
              </Icon>
            </div>
            {online && !disableSelectAll && (
              <div className={styles.checkContainer}>
                <Form.Check
                  value={allUsersSelected}
                  checked={allUsersSelected}
                  onChange={({ target: { checked } }) => {
                    if (checked) {
                      const targetUsers = !showAllUsers
                        ? workspaceFilteredUsers
                        : allFilteredUsers;

                      if (onBulkAdd) {
                        onBulkAdd(targetUsers);
                      } else if (!stateUpdateOnly) {
                        bulkAddUsers({
                          variables: {
                            userIds: targetUsers?.map(
                              ({ id: userId }) => userId,
                            ),
                            parentId,
                            parentType,
                          },
                        }).then(() => setUsers([...selected, ...targetUsers]));
                      } else {
                        setUsers([...selected, ...targetUsers]);
                      }
                      setModalOpen(false);
                    } else {
                      if (onBulkRemove) {
                        onBulkRemove(selected);
                      } else if (!stateUpdateOnly) {
                        bulkRemoveUsers({
                          variables: {
                            userIds: selected?.map(({ id: userId }) => userId),
                            parentId,
                            parentType,
                          },
                        }).then(() => setUsers([]));
                      } else {
                        setUsers([]);
                      }
                    }
                  }}
                />
                <Text size="sm" noMargin weight="semiBold">
                  Select All
                </Text>
              </div>
            )}
            {multiWorkspaceUsers !== null ? (
              <div className={styles.userListContainer}>
                {multiWorkspaceUsers.map(({ workspace: ws, users }) => (
                  <div key={ws.id}>
                    <div className={styles.sectionLine} />
                    <Text
                      noMargin
                      size="sm"
                      weight="semiBold"
                      color="secondary"
                      textAlign="center"
                    >
                      {ws.title}
                    </Text>
                    <div className={styles.sectionLine} />
                    {users.map((user) => userCard(user, onUserAdded))}
                  </div>
                ))}
              </div>
            ) : !showAllUsers ? (
              <div className={styles.userListContainer}>
                {workspaceFilteredUsers?.map((user) =>
                  userCard(user, onUserAdded),
                )}
                {!!usersWithAddOption?.length && isAdmin ? (
                  <div className={styles.invite}>
                    <div>
                      <div className={styles.sectionLine} />
                      <Text
                        textAlign="center"
                        noMargin
                        size="sm"
                        weight="semiBold"
                      >{`Invite to ${
                        otherWorkspace?.title || workspace?.title || 'Workspace'
                      } `}</Text>
                      <div className={styles.sectionLine} />
                    </div>
                    {usersWithAddOption?.map((u) =>
                      userCard(
                        u,
                        () => {
                          openConfirmationModal({
                            variant: 'warning',
                            title: 'Add User to Workspace',
                            description: `Are you sure you want to add ${
                              u.firstName
                            } ${u.lastName} to ${
                              otherWorkspace?.title || workspace?.title
                            }?`,
                            onSubmit: async () => {
                              await addUser({
                                userId: u.id,
                                workspaceId:
                                  otherWorkspace?.id || workspace?.id,
                                onComplete: async () => {
                                  await onUserAdded(u);
                                  await refetchWorkspacePermission();
                                  await refetchAllUsers();
                                },
                              });
                            },
                          });
                        },
                        true,
                        otherWorkspace || workspace,
                      ),
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={styles.userListContainer}>
                {!!allFilteredUsers?.length ? (
                  allFilteredUsers?.map((user) =>
                    userCard(user, onUserAdded, false),
                  )
                ) : (
                  <div>
                    <Spacer />
                    <Text noMargin weight="semiBold" textAlign="center">
                      {t(keys.utilities.NO_USERS_FOUND)}
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </PortalMenu>
      )}
    </div>
  );
}
